import { BaseRepository, QueryOptions, RepositoryError } from './BaseRepository';

/**
 * Call/Interaction entity interface
 * Represents a unified call/interaction across all systems
 */
export interface Call {
  id: string;
  interaction_type: 'call' | 'sms' | 'email' | 'chat';
  call_type: 'inbound' | 'outbound' | null;
  agent_type: 'human' | 'ai' | 'system' | null;
  agent_id: string | null;
  ai_agent_id: string | null;
  customer_id: string | null;
  call_sid: string | null;
  from_number: string | null;
  to_number: string | null;
  direction: string | null;
  status: 'queued' | 'ringing' | 'in-progress' | 'completed' | 'no-answer' | 'busy' | 'failed' | null;
  disposition: string | null;
  start_time: string | null;
  end_time: string | null;
  duration_seconds: number | null;
  talk_time_seconds: number | null;
  hold_time_seconds: number | null;
  recording_url: string | null;
  transcript: string | null;
  summary: string | null;
  sentiment_score: number | null;
  intent_detected: string | null;
  topics: Record<string, any> | null;
  keywords: string[] | null;
  quality_score: number | null;
  compliance_flags: string[] | null;
  sw_call_id: string | null;
  twilio_call_sid: string | null;
  ai_call_id: string | null;
  parent_interaction_id: string | null;
  queue_id: string | null;
  campaign_id: string | null;
  source_system: string;
  created_at: string;
  updated_at: string;
}

/**
 * Filter options for call queries
 */
export interface CallFilters {
  search?: string;
  customerId?: string;
  agentId?: string;
  interactionType?: 'call' | 'sms' | 'email' | 'chat';
  callType?: 'inbound' | 'outbound';
  agentType?: 'human' | 'ai' | 'system';
  status?: 'queued' | 'ringing' | 'in-progress' | 'completed' | 'no-answer' | 'busy' | 'failed';
  startDateFrom?: string;
  startDateTo?: string;
  minDuration?: number;
  maxDuration?: number;
  hasSentiment?: boolean;
  minSentiment?: number;
  maxSentiment?: number;
  hasRecording?: boolean;
  hasTranscript?: boolean;
}

/**
 * Call statistics
 */
export interface CallStatistics {
  total: number;
  completed: number;
  missed: number;
  failed: number;
  totalDuration: number;
  averageDuration: number;
  totalTalkTime: number;
  averageTalkTime: number;
  humanAgentCalls: number;
  aiAgentCalls: number;
  averageSentiment: number;
  callsWithRecording: number;
  callsWithTranscript: number;
}

/**
 * Call Repository
 * 
 * Manages all call/interaction data access operations.
 * Consolidates calls, ai_calls, and sw_calls into unified interface.
 * 
 * @example
 * ```typescript
 * const callRepo = new CallRepository();
 * 
 * // Find calls by customer
 * const customerCalls = await callRepo.findByCustomer('customer-id');
 * 
 * // Find recent AI calls
 * const aiCalls = await callRepo.findByAgentType('ai', { limit: 50 });
 * 
 * // Get call statistics
 * const stats = await callRepo.getStatistics();
 * ```
 */
export class CallRepository extends BaseRepository<Call> {
  constructor() {
    super({
      tableName: 'interactions_unified', // Will be consolidated in Phase 3
      primaryKey: 'id'
    });
  }

  /**
   * Find calls by various filters
   * 
   * @param filters Call-specific filter options
   * @param options Query options (pagination, ordering)
   * @returns Promise resolving to array of matching calls
   */
  async findByFilters(
    filters: CallFilters,
    options?: QueryOptions
  ): Promise<Call[]> {
    let query = this.client.from(this.config.tableName).select('*');

    // Search across multiple fields
    if (filters.search) {
      query = query.or(`
        call_sid.ilike.%${filters.search}%,
        from_number.ilike.%${filters.search}%,
        to_number.ilike.%${filters.search}%,
        customer_id.ilike.%${filters.search}%
      `);
    }

    // Customer filter
    if (filters.customerId) {
      query = query.eq('customer_id', filters.customerId);
    }

    // Agent filter
    if (filters.agentId) {
      query = query.eq('agent_id', filters.agentId);
    }

    // Interaction type filter
    if (filters.interactionType) {
      query = query.eq('interaction_type', filters.interactionType);
    }

    // Call type filter
    if (filters.callType) {
      query = query.eq('call_type', filters.callType);
    }

    // Agent type filter
    if (filters.agentType) {
      query = query.eq('agent_type', filters.agentType);
    }

    // Status filter
    if (filters.status) {
      query = query.eq('status', filters.status);
    }

    // Date range filters
    if (filters.startDateFrom) {
      query = query.gte('start_time', filters.startDateFrom);
    }

    if (filters.startDateTo) {
      query = query.lte('start_time', filters.startDateTo);
    }

    // Duration filters
    if (filters.minDuration !== undefined) {
      query = query.gte('duration_seconds', filters.minDuration);
    }

    if (filters.maxDuration !== undefined) {
      query = query.lte('duration_seconds', filters.maxDuration);
    }

    // Sentiment filters
    if (filters.hasSentiment) {
      query = query.not('sentiment_score', 'is', null);
    }

    if (filters.minSentiment !== undefined) {
      query = query.gte('sentiment_score', filters.minSentiment);
    }

    if (filters.maxSentiment !== undefined) {
      query = query.lte('sentiment_score', filters.maxSentiment);
    }

    // Recording/transcript filters
    if (filters.hasRecording) {
      query = query.not('recording_url', 'is', null);
    }

    if (filters.hasTranscript) {
      query = query.not('transcript', 'is', null);
    }

    // Apply ordering
    if (options?.orderBy) {
      query = query.order(options.orderBy.column, { 
        ascending: options.orderBy.ascending 
      });
    } else {
      // Default: order by start time descending (most recent first)
      query = query.order('start_time', { ascending: false });
    }

    // Apply pagination
    if (options?.limit) {
      const from = options.offset || 0;
      const to = from + options.limit - 1;
      query = query.range(from, to);
    }

    const { data, error } = await query;

    if (error) {
      throw new RepositoryError(
        `Failed to fetch calls with filters`, 
        error
      );
    }

    return (data || []) as Call[];
  }

  /**
   * Get call statistics
   * 
   * @returns Promise resolving to call statistics
   */
  async getStatistics(): Promise<CallStatistics> {
    const { data, error } = await this.client
      .from(this.config.tableName)
      .select('status, duration_seconds, talk_time_seconds, agent_type, sentiment_score, recording_url, transcript');

    if (error) {
      throw new RepositoryError(
        `Failed to fetch call statistics`, 
        error
      );
    }

    const calls = data || [];
    const completedCalls = calls.filter(c => c.status === 'completed');

    return {
      total: calls.length,
      completed: completedCalls.length,
      missed: calls.filter(c => c.status === 'no-answer').length,
      failed: calls.filter(c => c.status === 'failed').length,
      totalDuration: calls.reduce((sum, c) => sum + (c.duration_seconds || 0), 0),
      averageDuration: completedCalls.length > 0
        ? calls.reduce((sum, c) => sum + (c.duration_seconds || 0), 0) / completedCalls.length
        : 0,
      totalTalkTime: calls.reduce((sum, c) => sum + (c.talk_time_seconds || 0), 0),
      averageTalkTime: completedCalls.length > 0
        ? calls.reduce((sum, c) => sum + (c.talk_time_seconds || 0), 0) / completedCalls.length
        : 0,
      humanAgentCalls: calls.filter(c => c.agent_type === 'human').length,
      aiAgentCalls: calls.filter(c => c.agent_type === 'ai').length,
      averageSentiment: calls.filter(c => c.sentiment_score !== null).length > 0
        ? calls.reduce((sum, c) => sum + (c.sentiment_score || 0), 0) / calls.filter(c => c.sentiment_score !== null).length
        : 0,
      callsWithRecording: calls.filter(c => c.recording_url !== null).length,
      callsWithTranscript: calls.filter(c => c.transcript !== null).length
    };
  }

  /**
   * Find calls by customer ID
   * 
   * @param customerId Customer ID to filter by
   * @param options Query options
   * @returns Promise resolving to array of calls
   */
  async findByCustomer(
    customerId: string,
    options?: QueryOptions
  ): Promise<Call[]> {
    let query = this.client
      .from(this.config.tableName)
      .select('*')
      .eq('customer_id', customerId);

    // Apply ordering (most recent first)
    query = query.order('start_time', { ascending: false });

    // Apply pagination
    if (options?.limit) {
      const from = options.offset || 0;
      const to = from + options.limit - 1;
      query = query.range(from, to);
    }

    const { data, error } = await query;

    if (error) {
      throw new RepositoryError(
        `Failed to find calls by customer`, 
        error
      );
    }

    return (data || []) as Call[];
  }

  /**
   * Find calls by agent ID
   * 
   * @param agentId Agent ID to filter by
   * @param options Query options
   * @returns Promise resolving to array of calls
   */
  async findByAgent(
    agentId: string,
    options?: QueryOptions
  ): Promise<Call[]> {
    let query = this.client
      .from(this.config.tableName)
      .select('*')
      .eq('agent_id', agentId);

    // Apply ordering
    if (options?.orderBy) {
      query = query.order(options.orderBy.column, { 
        ascending: options.orderBy.ascending 
      });
    } else {
      query = query.order('start_time', { ascending: false });
    }

    // Apply pagination
    if (options?.limit) {
      const from = options.offset || 0;
      const to = from + options.limit - 1;
      query = query.range(from, to);
    }

    const { data, error } = await query;

    if (error) {
      throw new RepositoryError(
        `Failed to find calls by agent`, 
        error
      );
    }

    return (data || []) as Call[];
  }

  /**
   * Find calls by agent type (human, ai, system)
   * 
   * @param agentType Agent type to filter by
   * @param options Query options
   * @returns Promise resolving to array of calls
   */
  async findByAgentType(
    agentType: 'human' | 'ai' | 'system',
    options?: QueryOptions
  ): Promise<Call[]> {
    return this.findByFilters({ agentType }, options);
  }

  /**
   * Find calls by status
   * 
   * @param status Call status to filter by
   * @param options Query options
   * @returns Promise resolving to array of calls
   */
  async findByStatus(
    status: 'queued' | 'ringing' | 'in-progress' | 'completed' | 'no-answer' | 'busy' | 'failed',
    options?: QueryOptions
  ): Promise<Call[]> {
    return this.findByFilters({ status }, options);
  }

  /**
   * Find call by call SID (Twilio identifier)
   * 
   * @param callSid Twilio call SID
   * @returns Promise resolving to call or null
   */
  async findByCallSid(callSid: string): Promise<Call | null> {
    const { data, error } = await this.client
      .from(this.config.tableName)
      .select('*')
      .eq('call_sid', callSid)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw new RepositoryError(
        `Failed to find call by SID`, 
        error
      );
    }

    return data as Call;
  }

  /**
   * Find recent calls (last N days)
   * 
   * @param days Number of days to look back
   * @param options Query options
   * @returns Promise resolving to array of calls
   */
  async findRecent(
    days: number = 7,
    options?: QueryOptions
  ): Promise<Call[]> {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    return this.findByFilters({
      startDateFrom: startDate.toISOString()
    }, options);
  }

  /**
   * Find calls with transcripts
   * 
   * @param options Query options
   * @returns Promise resolving to array of calls with transcripts
   */
  async findWithTranscripts(options?: QueryOptions): Promise<Call[]> {
    return this.findByFilters({ hasTranscript: true }, options);
  }

  /**
   * Find calls with negative sentiment
   * 
   * @param threshold Sentiment threshold (default: 0)
   * @param options Query options
   * @returns Promise resolving to array of calls
   */
  async findNegativeSentiment(
    threshold: number = 0,
    options?: QueryOptions
  ): Promise<Call[]> {
    return this.findByFilters({
      hasSentiment: true,
      maxSentiment: threshold
    }, options);
  }

  /**
   * Find calls by phone number (from or to)
   * 
   * @param phoneNumber Phone number to search
   * @param options Query options
   * @returns Promise resolving to array of calls
   */
  async findByPhoneNumber(
    phoneNumber: string,
    options?: QueryOptions
  ): Promise<Call[]> {
    let query = this.client
      .from(this.config.tableName)
      .select('*')
      .or(`from_number.eq.${phoneNumber},to_number.eq.${phoneNumber}`);

    // Apply ordering
    query = query.order('start_time', { ascending: false });

    // Apply pagination
    if (options?.limit) {
      const from = options.offset || 0;
      const to = from + options.limit - 1;
      query = query.range(from, to);
    }

    const { data, error } = await query;

    if (error) {
      throw new RepositoryError(
        `Failed to find calls by phone number`, 
        error
      );
    }

    return (data || []) as Call[];
  }

  /**
   * Get active calls (in-progress or ringing)
   * 
   * @param options Query options
   * @returns Promise resolving to array of active calls
   */
  async findActive(options?: QueryOptions): Promise<Call[]> {
    let query = this.client
      .from(this.config.tableName)
      .select('*')
      .in('status', ['in-progress', 'ringing']);

    // Apply ordering
    query = query.order('start_time', { ascending: false });

    // Apply pagination
    if (options?.limit) {
      const from = options.offset || 0;
      const to = from + options.limit - 1;
      query = query.range(from, to);
    }

    const { data, error } = await query;

    if (error) {
      throw new RepositoryError(
        `Failed to find active calls`, 
        error
      );
    }

    return (data || []) as Call[];
  }
}
